export type AssignmentWindow = {
  assignedAt?: string | null;
  unassignedAt?: string | null;
  locationId?: string | null;
  timezone?: string | null;
};

const toDate = (value?: string | Date | null) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const sortWindows = (windows: AssignmentWindow[]) =>
  [...windows].sort((a, b) => {
    const aTime = toDate(a.assignedAt)?.getTime() ?? 0;
    const bTime = toDate(b.assignedAt)?.getTime() ?? 0;
    return aTime - bTime;
  });

export const mergeAssignmentWindows = (
  windows: AssignmentWindow[]
): AssignmentWindow[] => {
  if (!windows.length) return [];
  const sorted = sortWindows(windows);
  const merged: AssignmentWindow[] = [];
  sorted.forEach((window) => {
    const nextStart = toDate(window.assignedAt);
    if (!nextStart) return;
    const nextEnd = toDate(window.unassignedAt);
    const last = merged[merged.length - 1];
    if (!last) {
      merged.push({
        assignedAt: window.assignedAt,
        unassignedAt: window.unassignedAt,
        locationId: window.locationId,
        timezone: window.timezone,
      });
      return;
    }
    const lastStart = toDate(last.assignedAt);
    const lastEnd = toDate(last.unassignedAt);
    if (!lastStart) {
      merged[merged.length - 1] = {
        assignedAt: window.assignedAt,
        unassignedAt: window.unassignedAt,
        locationId: window.locationId,
        timezone: window.timezone,
      };
      return;
    }
    if (!lastEnd || nextStart <= lastEnd) {
      const mergedEnd = !lastEnd || !nextEnd ? null : new Date(Math.max(lastEnd.getTime(), nextEnd.getTime()));
      merged[merged.length - 1] = {
        assignedAt: last.assignedAt,
        unassignedAt: mergedEnd ? mergedEnd.toISOString() : null,
        locationId: last.locationId || window.locationId || null,
        timezone: last.timezone || window.timezone || null,
      };
      return;
    }
    merged.push({
      assignedAt: window.assignedAt,
      unassignedAt: window.unassignedAt,
      locationId: window.locationId,
      timezone: window.timezone,
    });
  });
  return merged;
};

export const buildAssignmentWindows = ({
  machineAssignmentHistory,
  machineAssignmentInfo,
  machineIds,
}: {
  machineAssignmentHistory?: Record<string, AssignmentWindow[]>;
  machineAssignmentInfo?: Record<string, AssignmentWindow>;
  machineIds?: string[];
}) => {
  const windowsByMachine: Record<string, AssignmentWindow[]> = {};
  const ids = new Set<string>(machineIds || []);

  if (machineAssignmentHistory) {
    Object.entries(machineAssignmentHistory).forEach(([id, entries]) => {
      ids.add(id);
      windowsByMachine[id] = mergeAssignmentWindows(
        (entries || []).map((entry) => ({
          assignedAt: entry?.assignedAt ?? null,
          unassignedAt: entry?.unassignedAt ?? null,
        }))
      );
    });
  }

  if (machineAssignmentInfo) {
    Object.entries(machineAssignmentInfo).forEach(([id, entry]) => {
      ids.add(id);
      if (windowsByMachine[id]?.length) return;
      windowsByMachine[id] = mergeAssignmentWindows([
        {
          assignedAt: entry?.assignedAt ?? null,
          unassignedAt: entry?.unassignedAt ?? null,
        },
      ]);
    });
  }

  ids.forEach((id) => {
    if (!windowsByMachine[id]) windowsByMachine[id] = [];
  });

  const currentAssignmentByMachine: Record<string, AssignmentWindow | null> = {};
  Object.entries(windowsByMachine).forEach(([id, windows]) => {
    if (!windows.length) {
      currentAssignmentByMachine[id] = null;
      return;
    }
    const active = windows.find((entry) => !entry?.unassignedAt);
    if (active) {
      currentAssignmentByMachine[id] = active;
      return;
    }
    const last = [...windows].sort((a, b) => {
      const aTime = toDate(a?.unassignedAt)?.getTime() ?? 0;
      const bTime = toDate(b?.unassignedAt)?.getTime() ?? 0;
      return bTime - aTime;
    })[0];
    currentAssignmentByMachine[id] = last || null;
  });

  return {
    windowsByMachine,
    currentAssignmentByMachine,
  };
};
